import { useCallback, useMemo, useRef, useSyncExternalStore } from "react"

import { Filter, X } from "@/components/icons"
import { useTranslation } from "react-i18next"

import type { DashboardSpec, QueryBinding } from "../dashboard/catalog"

import { Button } from "@/components/ui/button"

import { useAutoRefreshOnFilterChange } from "../hooks/use-auto-refresh-on-filter-change"
import { useDashboardStateStore } from "../state/dashboard-state"

type FilterPill = {
  /** JSON-Pointer path in dashboard state (e.g. "/filters/region"). */
  path: string
  /** Human label, derived from the path's leaf segment. */
  label: string
  /** Current value as a display-friendly string. */
  display: string
}

const EMPTY_BINDINGS: QueryBinding[] = []

/**
 * Filter strip rendered above the dashboard grid.
 *
 * Surfaces every state path referenced by a query binding's
 * `params_from_state` map as a removable pill so users see *which*
 * filters are currently narrowing the data — same affordance Power BI
 * shows in its "Filters" pane. Clearing a pill writes `null` to the
 * path, which propagates through the auto-refresh hook to re-fetch
 * the affected bindings.
 *
 * Also mounts {@link useAutoRefreshOnFilterChange}; co-locating the
 * pill UI and the auto-refresh wire keeps both behaviours behind a
 * single context-aware component instead of threading them through
 * the renderer separately.
 */
export function DashboardFilterBar({
  spec,
  isRefreshing,
  onAutoRefresh,
}: {
  spec: DashboardSpec
  isRefreshing: boolean
  onAutoRefresh?: () => void | Promise<void>
}) {
  const { t } = useTranslation()
  const store = useDashboardStateStore()
  const bindings = spec.queryBindings ?? EMPTY_BINDINGS

  useAutoRefreshOnFilterChange({
    queryBindings: bindings,
    enabled: !!onAutoRefresh,
    onRefresh: onAutoRefresh ?? (() => undefined),
  })

  const watchedPaths = useMemo(() => {
    const paths = new Set<string>()
    for (const binding of bindings) {
      const params = binding.params_from_state
      if (!params) continue
      for (const path of Object.values(params)) {
        if (typeof path === "string" && path.length > 0) paths.add(path)
      }
    }
    return Array.from(paths)
  }, [bindings])

  // Subscribe to the dashboard state store so pill values stay live
  // as users mutate filters. useSyncExternalStore expects
  // getSnapshot to be referentially stable when nothing changed,
  // so we memoise the snapshot inside a ref — return the cached
  // object until at least one watched path actually differs.
  const snapshotCacheRef = useRef<Record<string, unknown>>({})
  const lastPathsRef = useRef<readonly string[]>([])
  const getSnapshot = useCallback(() => {
    if (lastPathsRef.current !== watchedPaths) {
      // Path set changed (typically because the spec re-loaded);
      // force a fresh snapshot so the cache key tracks the current
      // watched-path set.
      lastPathsRef.current = watchedPaths
      const next: Record<string, unknown> = {}
      for (const path of watchedPaths) next[path] = store.getDashState(path)
      snapshotCacheRef.current = next
      return next
    }
    const cached = snapshotCacheRef.current
    for (const path of watchedPaths) {
      const current = store.getDashState(path)
      if (!Object.is(cached[path], current)) {
        const next: Record<string, unknown> = { ...cached }
        for (const p of watchedPaths) next[p] = store.getDashState(p)
        snapshotCacheRef.current = next
        return next
      }
    }
    return cached
  }, [store, watchedPaths])
  const liveSnapshot = useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot)

  const pills = useMemo<FilterPill[]>(() => {
    return watchedPaths
      .map((path) => {
        const value = liveSnapshot[path]
        if (value === null || value === undefined || value === "") return null
        if (Array.isArray(value) && value.length === 0) return null
        const segments = path.split("/").filter(Boolean)
        const leaf = segments[segments.length - 1] ?? path
        const label = leaf
          .replace(/_/g, " ")
          .replace(/-/g, " ")
          .replace(/^[a-z]/, (c) => c.toUpperCase())
        const display = formatValue(value)
        return { path, label, display }
      })
      .filter((p): p is FilterPill => p !== null)
  }, [watchedPaths, liveSnapshot])
  void isRefreshing // reserved for future "filter pills disabled while refreshing"

  if (pills.length === 0) return null

  const handleClear = (path: string) => {
    store.setDashState(path, null)
  }

  const handleClearAll = () => {
    for (const pill of pills) store.setDashState(pill.path, null)
  }

  return (
    <div
      className="border-border bg-muted/30 flex flex-wrap items-center gap-2 border-b px-4 py-2"
      data-testid="dashboard-filter-bar"
    >
      <Filter aria-hidden className="text-muted-foreground size-4 shrink-0" />
      <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        {t("dash.filters.label", { defaultValue: "Filters" })}
      </span>
      <div className="flex flex-wrap items-center gap-1.5">
        {pills.map((pill) => (
          <span
            key={pill.path}
            className="bg-background inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs"
            data-testid={`dashboard-filter-pill-${pill.path}`}
          >
            <span className="text-muted-foreground">{pill.label}:</span>
            <span className="font-medium">{pill.display}</span>
            <button
              aria-label={t("dash.filters.clear", {
                defaultValue: "Clear filter {{label}}",
                label: pill.label,
              })}
              className="text-muted-foreground hover:text-foreground -my-1 -mr-2 flex size-6 items-center justify-center rounded-full"
              type="button"
              onClick={() => handleClear(pill.path)}
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
      </div>
      <Button
        className="text-muted-foreground hover:text-foreground ml-auto h-6 px-2 text-xs"
        size="sm"
        variant="ghost"
        onClick={handleClearAll}
      >
        {t("dash.filters.clear_all", { defaultValue: "Clear all" })}
      </Button>
    </div>
  )
}

function formatValue(value: unknown): string {
  if (Array.isArray(value)) {
    if (value.length <= 2) return value.map(formatValue).join(", ")
    return `${value.slice(0, 2).map(formatValue).join(", ")} +${value.length - 2}`
  }
  if (typeof value === "number") return value.toLocaleString()
  if (typeof value === "boolean") return value ? "true" : "false"
  if (value === null || value === undefined) return ""
  return String(value)
}
